// filepath: apps/web/src/features/admin/fieldVisibility/types.ts
//
// FR-UI-ADM-4 — TypeScript wire-type mirrors for the admin viewer
// (role × entity-type field classification). Mirrors the Go wire
// types in
//   internal/kernel/role_field_visibility_handler.go
// (RoleFieldVisibilityResponse, FieldVisibilityState).
//
// INV-9: all server-supplied strings render as text nodes only.

/**
 * FieldVisibilityState — per-field classification. The UI-side
 * mirror of the Go `FieldVisibilityState` type. Values match
 * server wire vocabulary verbatim so the SPA does not need a
 * translator between server and renderer.
 *
 *   - "visible"        — target role has read access on the entity
 *                        type, and no visibleWhen formula hides the
 *                        field. Default state for permitted fields.
 *   - "hidden_cosmetic" — (reserved wire value, see server note)
 *                        a visibleWhen formula would hide this field
 *                        for the target role. Value preserved
 *                        server-side (INV-2 / FR-AUTH-7 boundary).
 *                        In v1 the server emits "visible" for every
 *                        permitted field; this label appears once
 *                        FR-AUTH-6 ships field-level permissions.
 *   - "denied_security" — target role has NO read access on the
 *                        entity type; the field is server-stripped
 *                        (INV-2 / FR-AUTH-7 — security boundary).
 *
 * The DATA-STATE ATTRIBUTE carries the canonical value
 * (`visible | hidden_cosmetic | denied_security`) — see
 * FieldVisibilityStateCell.tsx.
 */
export type FieldVisibilityState =
  "visible" | "hidden_cosmetic" | "denied_security";

/**
 * Server wire-shape: `data-state` attribute value. Mirrors
 * FieldVisibilityState (the renderer maps one-to-one onto this
 * attribute).
 */
export const FIELD_VISIBILITY_DATA_STATE: Record<FieldVisibilityState, string> =
  {
    visible: "visible",
    hidden_cosmetic: "hidden_cosmetic",
    denied_security: "denied_security",
  };

/**
 * RoleFieldVisibilityResponse — GET /api/admin/role-field-visibility/{role}/{entityType}
 * wire shape. Mirrors RoleFieldVisibilityResponse in
 * internal/kernel/role_field_visibility_handler.go.
 */
export type RoleFieldVisibilityResponse = {
  role: string;
  entity_type: string;
  fields: Record<string, FieldVisibilityState>;
};

/**
 * RoleFieldVisibilityError — typed envelope for the 5 fetch error
 * states the viewer surfaces (FR-UI-CORE-10 AC-5 + AC-6 of FR-UI-ADM-4).
 * The page maps each `kind` to its dedicated error component.
 *
 *   - "fetch_failure"     — 5xx or generic network failure.
 *   - "permission_denied" — 403 (caller lacks auth.read on the tenant).
 *   - "stale_version"     — 410 / 404 carrying stale-version code.
 *   - "rate_limit"        — 429 backpressure.
 *   - "network"           — DNS / connection refused.
 */
export type RoleFieldVisibilityError = {
  kind:
    | "fetch_failure"
    | "permission_denied"
    | "rate_limit"
    | "stale_version"
    | "network";
  message: string;
  retriable: boolean;
};

/**
 * FR-UI-ADM-4 AC-6: root aria-label carries roleName + entityType.
 * The page constructs the label via i18n interpolation. This type
 * pins the substitution shape so the page and a11y test agree on
 * the field names.
 */
export interface FieldVisibilityAriaLabelProps {
  roleName: string;
  entityType: string;
}
